/**
 * helpContent.js — v2.0
 * Conteúdo contextual de ajuda para cada rota do CRM TOTVS Moda.
 * Chave: pathname (ex: '/dashboard')
 *
 * GERADO AUTOMATICAMENTE via DOC-54 — não editar manualmente.
 * Para atualizar: editar os arquivos helpContent.[slug].js e re-executar o merge.
 * Total de rotas: 42
 */
export const helpContent = {
  "/admin/notifications": {
    title: "Painel de Notificações",
    roles: ["admin"],
    description: "Gerencia os tipos de notificação, os canais de envio (Telegram, e-mail, WhatsApp) e o histórico de mensagens enviadas ao time comercial.",
    sections: [
      {
        title: "KPIs no topo",
        content: "Os quatro cartões mostram: status do bot Telegram, total de mensagens enviadas nas últimas 24h, falhas nesse mesmo período e quantidade de regras ativas. Falhas aparecem em vermelho e indicam que é necessário checar a aba Histórico.",
      },
      {
        title: "Aba Tipos & Regras",
        content: "Lista todas as regras de notificação cadastradas (ex.: lead vencido, novo lead atribuído). Cada regra tem um canal de destino, condições de disparo e pode ser ativada ou desativada individualmente. Use \"Novo tipo\" para criar uma regra personalizada.",
      },
      {
        title: "Aba Canais",
        content: "Exibe a configuração de cada canal (Telegram, e-mail, WhatsApp, in-app) e suas estatísticas de envio. O canal Telegram requer que a variável TELEGRAM_BOT_TOKEN esteja configurada no servidor.",
      },
      {
        title: "Aba Por Usuário",
        content: "Mostra as preferências de notificação de cada usuário e permite ajustar quais tipos de alerta cada um recebe.",
      },
      {
        title: "Abas Histórico e Métricas",
        content: "Histórico lista cada mensagem enviada com status de entrega (sucesso/falha) e timestamp. Métricas consolida taxas de entrega por canal e por período para análise de desempenho.",
      },
    ],
    tips: ["Verifique a aba Histórico sempre que os KPIs indicarem falhas nas últimas 24h.", "Uma regra desativada não dispara notificações, mas permanece cadastrada para reuso futuro.", "O bot Telegram precisa do token configurado no servidor — sem ele todas as notificações Telegram falham.", "Use \"Novo tipo\" com moderação: excesso de alertas reduz a atenção do time comercial.", "O cascade de canais é Telegram → e-mail → in_app — configure ao menos um canal ativo."],
  },

  "/admin/reconciliacao": {
    title: "Conciliação — Admin",
    roles: ["admin"],
    description: "Visão administrativa da conciliação de forecast: criação manual de lotes mensais, acompanhamento do progresso por ESN e resolução em lote dos itens pendentes.",
    sections: [
      {
        title: "Trigger manual",
        content: "Permite criar um lote de conciliação para um mês específico fora do ciclo automático. Selecione o mês (formato AAAA-MM) e clique em \"Criar Lote\". O sistema carrega todos os leads com previsão vencida naquele mês e gera os itens para revisão dos ESNs.",
      },
      {
        title: "Progresso por ESN",
        content: "Tabela que mostra, para cada ESN e mês, quantos itens foram gerados, quantos já foram resolvidos e quantos ainda estão pendentes. Itens pendentes aparecem em vermelho; o prazo (deadline) indica até quando o ESN deve responder.",
      },
      {
        title: "Status do lote",
        content: "\"Aberto\" = dentro do prazo, aguardando respostas. \"Vencido\" = prazo expirado com itens ainda pendentes. \"Concluído\" = todos os itens foram resolvidos.",
      },
      {
        title: "Resolver pendentes (em lote)",
        content: "O botão \"Resolver pendentes\" posterga automaticamente todos os itens não respondidos de um ESN em +1 mês. Use quando o prazo venceu e o ESN não finalizou a revisão a tempo.",
      },
      {
        title: "Histórico de lotes",
        content: "Lista todos os lotes criados com o mês de referência, a proporção de itens resolvidos e o status final. Útil para auditar a disciplina de forecast ao longo do tempo.",
      },
    ],
    tips: ["O lote mensal normalmente é criado automaticamente pelo cron — use o trigger manual apenas para reprocessar um mês ou criar lotes retroativos.", "Verifique ESNs com muitos \"pendentes\" antes de usar a resolução em lote para não postergar incorretamente leads já ganhos.", "Lotes com status \"Vencido\" indicam que o ESN não completou a revisão no prazo acordado.", "Um lote com 0 itens resolvidos pode indicar que o ESN ainda não acessou a tela de Conciliação."],
  },

  "/agenda": {
    title: "Agenda de Atividades",
    roles: ["esn", "manager", "admin"],
    description: "Visão consolidada de todas as ações e compromissos pendentes, organizados por data. Permite acompanhar o que precisa ser feito hoje, esta semana ou neste mês.",
    sections: [
      {
        title: "Filtro de período",
        content: "Use os botões \"Hoje\", \"Esta Semana\" e \"Este Mês\" no canto superior direito para alternar o período exibido. O contador no cabeçalho mostra quantas ações existem em cada visão.",
      },
      {
        title: "Ações vencidas",
        content: "Atividades com data no passado aparecem em destaque rosa no topo da lista, antes das demais. São as que precisam de atenção imediata.",
      },
      {
        title: "Grupos por data",
        content: "As atividades futuras são agrupadas por data (Hoje, Amanhã, dia da semana etc.) em ordem cronológica, facilitando a leitura do que vem a seguir.",
      },
      {
        title: "Concluir uma ação",
        content: "Clique no círculo à esquerda de uma ação para marcá-la como concluída. O item desaparece da lista após confirmação. Somente ações permitem essa interação — compromissos de fechamento e interações passadas são somente leitura.",
      },
      {
        title: "Informações do item",
        content: "Cada item exibe o nome do lead, descrição, canal (Telefone, WhatsApp, Reunião etc.), estágio atual e a qual funil pertence (Novos ou Base).",
      },
      {
        title: "Origem das atividades",
        content: "A agenda consolida dois tipos de item: ações agendadas nos leads (próximas tarefas) e previsões de fechamento. Ambos são criados dentro do lead, não diretamente na agenda.",
      },
    ],
    tips: ["Comece sempre pela seção \"Vencido\" — são os itens com maior urgência.", "O contador \"ações hoje\" no topo ajuda a planejar o dia sem precisar rolar a lista.", "Para criar uma nova ação, abra o lead correspondente e use a aba de atividades.", "A cor do ícone de canal indica o tipo de contato: azul = telefone/vídeo, verde = WhatsApp, roxo = e-mail.", "Leads marcados como \"Novos\" aparecem em azul; \"Base\" em roxo — fácil de identificar o funil de origem."],
  },

  "/apn/dashboard": {
    title: "APN Dashboard",
    roles: ["apn"],
    description: "Painel principal do Agente de Parceiro de Negócios (APN). Cadastre prospects captados em campo e acompanhe os que aguardam handoff para um ESN responsável.",
    sections: [
      {
        title: "Resumo de performance",
        content: "Os quatro indicadores no topo mostram: total de prospects captados, handoffs realizados, leads fechados como Ganho e a taxa de conversão geral. Atualizam a cada vez que a página é carregada.",
      },
      {
        title: "Cadastrar novo prospect",
        content: "Preencha o formulário lateral com Razão Social (obrigatório), CNPJ, Nome Fantasia, a principal dor relatada pelo cliente, a data/hora de agenda combinada e notas adicionais. Clique em \"Cadastrar Prospect\" para salvar.",
      },
      {
        title: "Dor levantada",
        content: "Campo de texto livre para registrar o problema ou necessidade que o cliente descreveu. Quanto mais detalhado, mais fácil será a qualificação pelo ESN durante o handoff.",
      },
      {
        title: "Lista de prospects aguardando handoff",
        content: "Todos os prospects cadastrados aparecem à direita, com o status \"Aguardando handoff\". Cada card exibe razão social, CNPJ, trecho da dor e a data de agenda, se informada.",
      },
      {
        title: "Realizar handoff para ESN",
        content: "Clique em \"Enviar para ESN\" no card do prospect, selecione o ESN responsável, adicione notas de transição e confirme. O lead é transferido e sai da sua lista.",
      },
    ],
    tips: ["Preencha a dor levantada com detalhes — isso acelera a qualificação pelo ESN.", "O campo de data/hora da agenda ajuda o ESN a se preparar para a reunião já agendada.", "Após o handoff, o prospect some da lista — acompanhe o resultado em Métricas APN.", "Cadastre apenas prospects com interesse real confirmado para manter a qualidade do funil."],
  },

  "/apn/metrics": {
    title: "Métricas APN",
    roles: ["apn"],
    description: "Visualize os indicadores de desempenho da sua atuação como Agente de Parceiro de Negócios: captações, handoffs realizados, ganhos e taxa de conversão.",
    sections: [
      {
        title: "Total captados",
        content: "Número total de prospects que você cadastrou no CRM como APN. Inclui todos os prospects, independentemente do status atual no funil.",
      },
      {
        title: "Handoffs realizados",
        content: "Quantidade de prospects que você transferiu para um ESN responsável. Um handoff representa a passagem formal da oportunidade para o time de vendas.",
      },
      {
        title: "Fechado como Ganho",
        content: "Total de prospects originalmente captados por você que chegaram ao stage \"Ganho\" no funil. Indica o impacto real das suas captações no resultado comercial.",
      },
      {
        title: "Taxa de conversão",
        content: "Percentual calculado entre prospects captados e os que foram fechados como Ganho. Reflete a qualidade das oportunidades identificadas.",
      },
    ],
    tips: ["Uma taxa de conversão saudável começa com prospects bem qualificados na captação.", "Se o número de handoffs for muito menor que o de captados, verifique prospects parados no dashboard.", "Os indicadores refletem o histórico completo — não apenas o mês atual."],
  },

  "/audit-log": {
    title: "Log de Atividades",
    roles: ["admin", "manager"],
    description: "Registro completo de todas as alterações realizadas no sistema, com rastreabilidade por usuário, entidade e campo. Útil para auditoria e investigação de mudanças.",
    sections: [
      {
        title: "Entidades rastreadas",
        content: "Lead, Conta, ESN, Usuário e Produto. Toda criação, edição, exclusão ou mudança de etapa nessas entidades gera um registro no log.",
      },
      {
        title: "Tipos de ação",
        content: "Criação (verde): novo registro inserido.\nEdição (ciano): campo alterado — exibe valor anterior e novo valor.\nExclusão (rosa): registro removido.\nMudança de Etapa (laranja): lead movido entre estágios do funil.",
      },
      {
        title: "Filtros disponíveis",
        content: "Filtre por entidade, tipo de ação, usuário específico e intervalo de datas (De / Até).\nUse o campo de busca de texto para pesquisar por valor anterior ou novo valor. Pressione Enter ou clique na lupa para aplicar.",
      },
      {
        title: "Paginação",
        content: "Os registros são exibidos em páginas de 50 itens. Navegue com os botões \"Anterior\" e \"Próxima\" no rodapé da tabela.\nO total de registros encontrados é exibido no cabeçalho da página.",
      },
    ],
    tips: ["Use o filtro de usuário para investigar quais alterações foram feitas por uma pessoa específica.", "O tipo \"Mudança de Etapa\" mostra de qual estágio o lead saiu e para qual foi.", "Clique em \"Limpar\" para remover todos os filtros ativos de uma só vez.", "O botão \"Atualizar\" recarrega os dados sem limpar os filtros aplicados.", "Registros de Conta incluem status de Ativo/Inativo/Cancelado como parte do histórico de edições."],
  },

  "/automations": {
    title: "Automações",
    roles: ["admin"],
    description: "Crie regras \"se isto → então aquilo\" para que o CRM aja automaticamente sobre leads: envie alertas, mova stages, crie tarefas ou altere a temperatura sem intervenção manual.",
    sections: [
      {
        title: "Lista de regras",
        content: "As automações são exibidas em dois grupos: Ativas e Inativas. Cada card mostra o gatilho (QUANDO), a ação (ENTÃO) e o funil ao qual se aplica. O contador de execuções indica quantas vezes a regra já disparou.",
      },
      {
        title: "Criar nova automação",
        content: "Clique em \"Nova Automação\". O builder tem 3 passos: (1) QUANDO — escolha o gatilho e configure parâmetros; (2) ENTÃO — escolha a ação e configure a mensagem ou destino; (3) ESCOPO — dê um nome, descrição e escolha o funil.",
      },
      {
        title: "Gatilhos disponíveis",
        content: "Parado no stage por X dias, sem interação por X dias, valor SaaS acima/abaixo de R$, BANT score baixo, previsão vencida, lead entrou em stage específico, lead foi criado. Configure os parâmetros de cada gatilho no passo 1.",
      },
      {
        title: "Ações disponíveis",
        content: "Notificar o ESN responsável, notificar por perfil (gerente/admin/ESN), mover para outro stage, criar tarefa com prazo, enviar alerta no Telegram (suporta variáveis {{razao_social}} e {{stage}}), alterar temperatura do lead.",
      },
      {
        title: "Ativar, editar e excluir",
        content: "Use o toggle em cada card para ativar/desativar sem excluir a regra. Clique em \"Editar\" para reabrir o builder com os dados atuais. Excluir é permanente — confirme a ação no popup.",
      },
      {
        title: "Histórico de execuções",
        content: "Clique em \"Histórico\" no card da regra para ver cada lead que disparou a automação, o stage no momento, quando ocorreu e o resultado (sucesso ou erro).",
      },
    ],
    tips: ["Comece com \"Sem interação por 7 dias → Notificar ESN\" para cobrir o caso mais comum de lead esfriando.", "Use o campo funil no passo 3 para criar regras exclusivas de Novos Nomes ou Base Instalada.", "Automações inativas são salvas com configuração intacta — reative quando precisar sem reconfigurar.", "Variáveis {{razao_social}} e {{stage}} no Telegram tornam a mensagem contextual para cada lead.", "Consulte o histórico antes de excluir uma regra — ele confirma se estava funcionando como esperado."],
  },

  "/base": {
    title: "Funil Base",
    roles: ["esn", "manager", "admin"],
    description: "Pipeline de expansão para clientes que já usam Totvs Moda — upsell, cross-sell e renovações. Acompanhe oportunidades na base instalada desde a qualificação até o fechamento.",
    sections: [
      {
        title: "Criando um lead",
        content: "Clique em \"+ Novo Lead\" na barra de filtros. Digite o CNPJ para buscar os dados da Receita Federal automaticamente (Razão Social e Nome Fantasia são preenchidos). Preencha ESN, produto, valores e previsão de fechamento, depois salve.",
      },
      {
        title: "Navegando no Kanban",
        content: "As colunas seguem as etapas do funil Base: Prospecção → Qualificado - Em levantamento → Negociação → Ganho / Perdido / Postergado. Arraste o card para mover o lead entre etapas. Em desktop, clicar no card abre o painel rápido (Quick View) à direita.",
      },
      {
        title: "Quick View (desktop)",
        content: "Painel lateral de 70% que abre ao clicar em qualquer card no kanban. Exibe dados resumidos e permite registrar interações, gerenciar tarefas e mudar o stage sem abrir o modal completo. Para editar todos os campos do lead, clique no botão \"Editar\" dentro do Quick View.",
      },
      {
        title: "Modal completo de lead — abas",
        content: "O modal abre ao clicar em \"Editar\" no Quick View (ou ao clicar em um card no mobile). Abas disponíveis: Dados (formulário completo com CNPJ, ESN, produto, valores, Origem e campos personalizados), Contatos, Qualificação (score BANT automático até 100 pts), Interações (ligação, WhatsApp, e-mail, reunião, nota), Propostas (com portal público e assinatura digital), Histórico (log de alterações de campos), Timeline (feed unificado filtrável) e ✨ IA. As últimas cinco abas ficam disponíveis somente após o lead ser salvo pela primeira vez.",
      },
      {
        title: "Filtros e Smart Views",
        content: "Use a FilterBar para filtrar por ESN, produto, stage ou previsão. Para salvar o conjunto atual de filtros como uma view nomeada, clique em \"Salvar View\" — você pode compartilhar a view com toda a equipe. Views salvas aparecem como chips clicáveis acima da barra de filtros.",
      },
      {
        title: "Visualização em tabela e exportação",
        content: "Alterne entre kanban e tabela usando o toggle de visualização na FilterBar. Na tabela é possível ordenar colunas e editar vários leads em sequência. Para exportar os leads com os filtros ativos para CSV, clique em \"Exportar CSV\".",
      },
    ],
    tips: ["Ao mover para Postergado, o sistema exige que você escolha uma data futura — esse lead voltará automaticamente para Prospecção quando o mês chegar.", "Campos obrigatórios configurados pelo admin bloqueiam a movimentação: um modal aparece pedindo que você preencha os dados antes de avançar.", "A aba \"Histórico\" registra alterações de campo (quem mudou o quê e quando) — é diferente da \"Timeline\", que mostra toda a comunicação com o cliente.", "Use a aba \"Propostas\" para enviar links públicos e solicitar assinatura digital diretamente pelo CRM, sem precisar de ferramenta externa.", "Use Smart Views para salvar filtros frequentes, como \"Clientes em Negociação com previsão neste mês\"."],
  },

  "/billing": {
    title: "Billing e Plano",
    roles: ["admin"],
    description: "Acompanhe o status da assinatura, gerencie o plano contratado e consulte o histórico de faturas do seu tenant no CRM.",
    sections: [
      {
        title: "Plano atual",
        content: "O card superior exibe o plano contratado (Trial, Starter, Pro ou Enterprise), o número de usuários ativos versus o limite do plano, e a data da próxima cobrança ou encerramento do trial.",
      },
      {
        title: "Status da assinatura",
        content: "Possíveis status: Ativo (pagamento em dia), Trial (período gratuito), Pagamento pendente (fatura em atraso), Cancelado e Trial expirado. Ação necessária é indicada por alerta visual.",
      },
      {
        title: "Fazer upgrade de plano",
        content: "Se o plano não for \"Ativo\", os cards de upgrade são exibidos com preços e limites de usuários. Clique em \"Selecionar\" para ser redirecionado ao checkout seguro via Stripe.",
      },
      {
        title: "Histórico de faturas",
        content: "Lista todas as faturas geradas com data, valor pago e status. Clique no ícone de link externo para abrir a fatura completa no portal do Stripe.",
      },
      {
        title: "Cancelar assinatura",
        content: "Disponível apenas para assinaturas ativas. Após cancelar, os dados são mantidos por 30 dias. É possível reativar dentro desse prazo sem perda de informações.",
      },
    ],
    tips: ["O plano Enterprise não tem limite de usuários e requer contato com vendas.", "O checkout é processado pelo Stripe — o CRM não armazena dados de cartão.", "Se o status for \"Pagamento pendente\", atualize o método de pagamento diretamente no link da fatura.", "Ultrapassar o limite de usuários do plano impede a criação de novos acessos."],
  },

  "/churn": {
    title: "Risco de Churn",
    roles: ["manager", "admin"],
    description: "Monitora clientes da base instalada com sinais de desengajamento, classificando-os em Alto, Médio e Baixo risco. Permite priorizar ações de retenção antes que o cliente se perca.",
    sections: [
      {
        title: "Cards de resumo",
        content: "Quatro cards no topo mostram a quantidade de leads em cada nível de risco (Alto, Médio e Baixo) e o valor total de SaaS mensal em risco (soma dos leads Alto + Médio). Recarregue com o botão de atualização.",
      },
      {
        title: "Filtro por nível de risco",
        content: "Use as pílulas \"Todos\", \"Alto Risco\", \"Médio Risco\" e \"Baixo Risco\" para focar em um grupo específico. O padrão exibe todos os leads da base com algum sinal de risco.",
      },
      {
        title: "Como o risco é calculado",
        content: "O score (0–100) combina: última interação há muito tempo (+30), lead postergado (+25), previsão de fechamento vencida (+20), temperatura \"Frio\" (+15) e ausência de próxima ação (+10). Score ≥60 = Alto, ≥35 = Médio, <35 = Baixo.",
      },
      {
        title: "Tags de motivo",
        content: "Cada lead exibe tags coloridas listando os fatores que elevaram o risco dele (ex: \"Sem interação recente\", \"Stage Postergado\"). São os pontos de ação concretos para a abordagem de retenção.",
      },
      {
        title: "Barra de score",
        content: "A barra colorida abaixo do nome do lead mostra visualmente a intensidade do risco. Rosa = alto, laranja = médio, verde = baixo. O número à direita é o score exato.",
      },
      {
        title: "Agendar contato (ESN)",
        content: "Para usuários ESN, leads de Alto ou Médio risco exibem o botão \"Agendar Contato\" que redireciona ao funil Base para registrar uma nova ação de acompanhamento.",
      },
    ],
    tips: ["Priorize os leads de Alto Risco com maior valor de SaaS — o impacto financeiro de perdê-los é maior.", "Tags como \"Sem próxima ação\" são as mais fáceis de resolver: basta agendar uma atividade no lead.", "Revise a lista semanalmente; scores mudam conforme novas interações são registradas nos leads.", "Um lead postergado com forecast vencido sem interação recente tende a chegar facilmente a Alto Risco.", "Clique no ícone ⓘ ao lado da barra para ver o detalhamento completo dos critérios de cálculo."],
  },

  "/contas": {
    title: "Contas (PJ)",
    roles: ["esn", "manager", "admin"],
    description: "Cadastro de empresas clientes (base instalada e pipeline). Cada conta tem um status de relacionamento — Ativo, Inativo ou Cancelado — que pode ser alterado a qualquer momento sem excluir o registro.",
    sections: [
      {
        title: "Painel de KPIs",
        content: "O topo da página exibe quatro indicadores: Total de contas, Ativas, Canceladas e Página atual. Os valores se ajustam conforme os filtros ativos.",
      },
      {
        title: "Filtrar por status",
        content: "Use os botões de status (Todos / Ativos / Inativos / Cancelados) para segmentar a lista. Por padrão a tela carrega somente contas Ativas.",
      },
      {
        title: "Outros filtros",
        content: "Combine a busca por nome ou CNPJ com os seletores de ESN e UF (estado) para localizar contas de um revendedor específico ou de uma região.",
      },
      {
        title: "Alterar status de uma conta",
        content: "Na coluna de ações, clique no ícone de toggle (⇄) para abrir o modal de status. Escolha Ativo, Inativo ou Cancelado; ao cancelar, o campo Motivo torna-se obrigatório.",
      },
      {
        title: "Editar dados da conta",
        content: "Clique no ícone de lápis ou diretamente na linha (mobile) para abrir o formulário de edição da conta com todos os campos e abas disponíveis.",
      },
      {
        title: "Criar nova conta",
        content: "Use o botão \"Nova Conta\" no cabeçalho. O formulário aceita CNPJ para preenchimento automático de razão social e endereço via consulta à Receita Federal.",
      },
    ],
    tips: ["Ao cancelar uma conta, preencha o motivo com detalhes — esse campo fica visível no histórico e é útil para análises de churn.", "Contas inativas continuam no cadastro e podem ser reativadas a qualquer momento alterando o status.", "O filtro de status padrão é \"Ativos\"; mude para \"Todos\" antes de exportar se precisar de toda a base.", "Managers e admins podem excluir contas; ESNs têm acesso de leitura e edição, mas não de exclusão.", "Use o filtro de UF para acompanhar carteiras regionais ou distribuir contas entre ESNs por estado."],
  },

  "/cross-sell": {
    title: "Hub Cross-sell",
    roles: ["manager", "admin"],
    description: "Exibe clientes da base instalada que já têm produtos Totvs ganhos e podem adquirir outros produtos do portfólio. Use esta tela para priorizar abordagens de expansão de carteira.",
    sections: [
      {
        title: "Painel de potencial",
        content: "O topo da tela mostra os 5 produtos com mais oportunidades de cross-sell na base, com o número de contas que ainda não os possuem e o percentual de penetração atual.",
      },
      {
        title: "Lista de oportunidades",
        content: "Cada linha representa uma conta com produtos já ganhos (em verde) e as sugestões de novos produtos (em roxo). Clique em qualquer linha para ver detalhes da conta.",
      },
      {
        title: "Buscar e filtrar",
        content: "Use o campo de busca para encontrar uma conta por razão social, nome fantasia ou CNPJ. Filtre por status do cliente (ativo, inativo, cancelado) ou por produto sugerido específico.",
      },
      {
        title: "Status do cliente",
        content: "O badge de status reflete a situação atual da conta na base: Ativo (verde), Inativo (laranja) ou Cancelado (rosa). Priorize contas ativas para abordagem imediata.",
      },
      {
        title: "Coluna ESN",
        content: "Visível apenas para gerentes e admins. Indica qual revendedor (ESN) é responsável pela conta, facilitando a delegação da abordagem de cross-sell.",
      },
    ],
    tips: ["Filtre por \"Ativo\" para focar nas contas com maior chance de conversão.", "Produtos com alta contagem no painel do topo são as melhores apostas de cross-sell.", "Use o filtro de produto sugerido para preparar abordagens segmentadas por solução.", "Contas com muitos produtos já ganhos costumam ter maior receptividade a novas soluções.", "Clientes inativos ou cancelados podem precisar de ação de recuperação antes do cross-sell."],
  },

  "/custom-fields": {
    title: "Campos Customizados",
    roles: ["admin"],
    description: "Crie campos extras para Leads e Contas, além dos campos padrão do CRM. Os campos aparecem nos formulários e, opcionalmente, na tabela de listagem e nos cards do kanban.",
    sections: [
      {
        title: "Alternar entre Lead e Conta",
        content: "Use os botões no topo da página para alternar entre os campos de Lead e de Conta. Cada entidade tem sua própria lista de campos customizados.",
      },
      {
        title: "Criar um campo",
        content: "Clique em \"Novo Campo\", defina o nome de exibição e escolha o tipo (Texto, Número, Data, Seleção única, Seleção múltipla, Sim/Não, URL, E-mail ou Telefone). O slug é gerado automaticamente a partir do nome.",
      },
      {
        title: "Opções para campos de seleção",
        content: "Para tipos \"Seleção única\" e \"Seleção múltipla\", adicione as opções disponíveis uma a uma. Pressione Enter ou clique no botão \"+\" para incluir cada opção.",
      },
      {
        title: "Visibilidade do campo",
        content: "Ao criar ou editar um campo, marque \"Mostrar na tabela\" para incluí-lo nas colunas da listagem, e \"Mostrar no card\" para exibi-lo nos cards do kanban. \"Obrigatório\" exige preenchimento no formulário.",
      },
      {
        title: "Reordenar campos",
        content: "Na versão desktop, arraste as linhas pelo ícone de alça (seis pontos) para definir a ordem em que os campos aparecem nos formulários. A nova ordem é salva automaticamente ao soltar.",
      },
      {
        title: "Desativar e excluir",
        content: "Clicar no ícone de toggle desativa o campo sem excluir os valores já salvos. Clicar na lixeira remove o campo permanentemente, mas os valores históricos são preservados no banco.",
      },
    ],
    tips: ["O tipo de campo não pode ser alterado após a criação — escolha com cuidado.", "Use \"Seleção única\" para garantir padronização de dados como segmento ou porte.", "Campos obrigatórios aumentam a qualidade dos dados, mas podem atrasar o cadastro.", "Ative \"Mostrar no card\" apenas para campos estratégicos, para não sobrecarregar o kanban.", "Desative campos temporariamente em vez de excluir — os dados históricos são preservados."],
  },

  "/dashboard": {
    title: "Dashboard",
    roles: ["esn", "manager", "admin"],
    description: "Visão consolidada do pipeline de vendas com KPIs em tempo real, ranking de ESNs e alertas de oportunidades com previsão próxima. Gerentes e admins veem dados de toda a equipe; ESNs veem sua própria carteira e metas.",
    sections: [
      {
        title: "KPIs Principais (4 tiles com gráfico)",
        content: "Os quatro indicadores em destaque são: Pipeline SaaS (valor total em aberto), Fechado SaaS (receita acumulada com tendência do mês), Conversão (% de leads ganhos sobre encerrados) e Ciclo médio (dias da criação ao fechamento — laranja quando acima de 42 dias).",
      },
      {
        title: "KPIs Secundários (6 compactos)",
        content: "Abaixo dos tiles principais: Leads ativos, Fechados no mês, Receita mês, Ticket médio, Sales Velocity e Ganho no Q (trimestre atual). Esses números refletem a carteira do período corrente.",
      },
      {
        title: "Pipeline por Etapa e Motivos de Perda",
        content: "Mini gráfico de barras mostrando contagem de leads e valor SaaS por etapa do funil. Barras em ciano são do funil Novos; verdes são do funil Base. Abaixo aparecem as top 5 tags de motivo de perda com suas contagens.",
      },
      {
        title: "Ranking ESN (gerentes e admins) ou Minhas Metas (ESN)",
        content: "Gerentes e admins veem até 6 ESNs com barra de progresso pipeline vs meta e seletor de período (30d / 90d / 6m / 1a). ESNs veem o próprio pipeline vs meta mensal. O seletor de período afeta somente este painel.",
      },
      {
        title: "Sentinelas",
        content: "Dois painéis lado a lado com leads cuja previsão de fechamento está no mês atual (laranja) ou no próximo mês (azul). Use para priorizar negociações que precisam ser aceleradas. Exibe até 10 oportunidades.",
      },
      {
        title: "Barra de Sales Velocity",
        content: "Rodapé informativo que mostra o valor médio gerado por dia pelo pipeline, calculado com base em leads ativos, ticket médio e taxa de conversão dos últimos 90 dias. Aparece somente quando há dados suficientes.",
      },
    ],
    tips: ["O ciclo médio em laranja (acima de 42 dias) indica que negociações estão se arrastando — revise os leads mais antigos.", "O seletor de período (30d/90d/6m/1a) no canto superior direito afeta apenas o Ranking ESN, não os KPIs principais.", "Clique em \"Ver análise completa\" no Ranking ESN para acessar a página de desempenho detalhado por ESN.", "Leads nos Sentinelas do mês atual precisam de atenção imediata — a previsão de fechamento é este mês.", "ESNs veem \"Sua carteira e metas\" em vez do ranking da equipe — os dados são filtrados automaticamente."],
  },

  "/database": {
    title: "Banco de Dados",
    roles: ["esn", "manager", "admin"],
    description: "Lista unificada de todos os leads dos funis New Names e Base em uma única tabela paginada. Use para buscar, filtrar e exportar oportunidades sem precisar alternar entre funis.",
    sections: [
      {
        title: "Busca e filtros",
        content: "Use a barra de busca para encontrar por título, razão social, CNPJ ou contato. Combine com os seletores de Funil (New Names/Base), Fase, ESN e Origem para refinar os resultados.",
      },
      {
        title: "Filtro por Origem",
        content: "O seletor \"Todas as Origens\" lista as origens cadastradas em /admin/lead-origins. Selecione uma origem para ver apenas leads captados por aquele canal.",
      },
      {
        title: "Exportar resultados",
        content: "Clique em \"Exportar Excel\" para baixar os leads visíveis com os filtros ativos. O botão fica desabilitado quando não há registros na página.",
      },
      {
        title: "Importar via e-mail",
        content: "O botão \"Importar (e-mail)\" redireciona para o fluxo de importação de leads recebidos via e-mail TOTVS. Use quando precisar registrar oportunidades enviadas por e-mail.",
      },
      {
        title: "Editar um lead",
        content: "Clique em qualquer linha da tabela para abrir o formulário completo de edição do lead. Todas as abas (Dados, Contatos, Qualificação, Interações, etc.) estão disponíveis.",
      },
      {
        title: "Paginação",
        content: "A tabela exibe até 50 registros por página. Use as setas no rodapé para navegar; o contador mostra a página atual e o total de registros.",
      },
    ],
    tips: ["Selecione um funil antes de escolher uma fase para ver somente as fases daquele funil.", "Os filtros são acumulativos — combine ESN + Origem para cruzar canal de captação com executivo.", "O total de registros exibido no cabeçalho reflete os filtros ativos, não o total do banco.", "Para criar um lead diretamente daqui, use o botão \"Novo Lead\" no cabeçalho da página."],
  },

  "/duplicates": {
    title: "Duplicatas",
    roles: ["admin"],
    description: "Detecta pares de leads potencialmente duplicados usando comparação automática de dados como razão social e CNPJ. Permite mesclar os dois leads em um único registro ou ignorar o par.",
    sections: [
      {
        title: "Filtro de confiança",
        content: "Use o controle deslizante para definir o percentual mínimo de confiança da detecção (70% a 100%). Valores mais altos exibem apenas duplicatas com alta certeza.",
      },
      {
        title: "Lista de pares suspeitos",
        content: "Cada card mostra dois leads lado a lado com o percentual de confiança e o motivo da detecção (ex: mesmo CNPJ, nome similar). Leads com maior confiança aparecem destacados em vermelho.",
      },
      {
        title: "Mesclar leads",
        content: "Clique em \"Mesclar\" para abrir o modal de mesclagem. Escolha qual lead será o principal (mantido) e selecione campo a campo qual valor preservar quando os dados forem diferentes.",
      },
      {
        title: "O que acontece na mesclagem",
        content: "Interações, propostas e tarefas do lead descartado são transferidas para o lead principal. O lead descartado é marcado como Perdido automaticamente.",
      },
      {
        title: "Ignorar um par",
        content: "Clique em \"Ignorar\" para remover o par da lista sem mesclar. O par não reaparece na sessão atual, mas pode voltar em análises futuras se os dados não mudarem.",
      },
    ],
    tips: ["Comece com confiança em 95%+ para tratar primeiro os casos mais óbvios.", "Verifique o motivo da detecção antes de mesclar — pode ser coincidência de nome.", "Prefira manter o lead com mais interações e histórico como o principal.", "Campos iguais nos dois leads são preservados automaticamente, sem escolha necessária.", "Use o botão de atualizar no cabeçalho para reanalisar após fazer mesclagens."],
  },

  "/esn-performance": {
    title: "Performance ESN",
    roles: ["manager", "admin"],
    description: "Ranking comparativo dos ESNs com métricas de SaaS fechado, ciclo médio e taxa de conversão. Use para identificar os melhores desempenhos e os que precisam de atenção.",
    sections: [
      {
        title: "KPIs do time",
        content: "Cinco indicadores no topo: ESNs ativos, total de SaaS fechado, oportunidades ativas, conversão média e ciclo médio do time. Ciclo médio em laranja indica que está acima da meta de 35 dias.",
      },
      {
        title: "Ranking ESN",
        content: "Tabela ordenada por SaaS fechado no período. Cada linha mostra leads ativos, fechado SaaS, ciclo médio, taxa de conversão e percentual de atingimento da meta. O pódio (1º, 2º, 3º) é destacado visualmente.",
      },
      {
        title: "Top ESN do período",
        content: "Destaque automático para o ESN com maior SaaS fechado. Exibe fechamentos, conversão, leads ativos e interações dos últimos 30 dias.",
      },
      {
        title: "Atenção necessária",
        content: "Lista os ESNs com alerta de baixa atividade. O sistema sugere conversa 1:1 quando o volume de interações está abaixo do esperado.",
      },
      {
        title: "Atividade por canal",
        content: "Distribuição de interações por WhatsApp, e-mail, ligação e reunião para cada ESN. Mostra o canal preferido de cada um e o total do período.",
      },
      {
        title: "Filtro de período",
        content: "Botões no canto superior direito para alternar entre 30 dias, 90 dias, 6 meses e 1 ano. Todos os dados e rankings são recalculados ao trocar o período.",
      },
    ],
    tips: ["O ciclo médio em laranja indica ESN acima da meta de 35 dias — priorize coaching nesse ponto.", "Use o filtro de 30 dias para decisões imediatas e 90 dias para análise de tendência.", "ESNs na seção \"Atenção necessária\" estão com poucas interações — contato proativo recomendado.", "A barra \"% Meta\" fica verde ao atingir 100% e laranja abaixo de 70%.", "Interações dos últimos 30 dias no card do Top ESN ajudam a entender o ritmo de trabalho."],
  },

  "/esns-admin": {
    title: "Gestão de ESNs",
    roles: ["admin"],
    description: "Cadastro e gerenciamento dos ESNs (executivos de vendas parceiros). Permite criar, editar e excluir ESNs, e acompanhar o ranking de desempenho mensal.",
    sections: [
      {
        title: "Cadastro de ESNs",
        content: "Lista todos os ESNs com nome e e-mail. Clique no ícone de lápis para editar inline diretamente na linha. Clique no ícone de lixeira para excluir — uma confirmação é exigida antes de remover.",
      },
      {
        title: "Criar novo ESN",
        content: "Clique em \"Novo ESN\" para exibir o formulário de criação. Informe o nome (obrigatório) e o e-mail (opcional). Confirme clicando em \"Criar\". O novo ESN aparece imediatamente na lista.",
      },
      {
        title: "Ranking do Mês (Leaderboard)",
        content: "Seção com ícone de troféu que exibe o ranking de desempenho dos ESNs no mês selecionado. Cada linha mostra a posição (🥇 🥈 🥉 ou número), o nome do ESN, a contagem de negócios ganhos, o valor total de SaaS ganho e os badges conquistados no mês.",
      },
      {
        title: "Seletor de mês do ranking",
        content: "Use o campo de mês acima do ranking para navegar entre períodos. O padrão é o mês atual. Selecione qualquer mês passado para ver o histórico de posições e badges.",
      },
    ],
    tips: ["O nome do ESN é usado em todos os relatórios e no funil — mantenha-o padronizado.", "Badges no ranking são concedidos automaticamente pelo sistema de gamificação ao atingir marcos de desempenho.", "Para definir metas de SaaS e Adesão por ESN, acesse a tela Planejamento de Metas (/metas).", "Excluir um ESN não remove seus leads históricos, mas desvincula futuros registros."],
  },

  "/esns": {
    title: "Meta ESN",
    roles: ["manager", "admin"],
    description: "Acompanhamento de metas e realizado por executivo (ESN), organizado em grid de 3 produtos × 12 meses com subtotais trimestrais. Use para avaliar performance individual e da equipe ao longo do ano.",
    sections: [
      {
        title: "Navegação por ano e funil",
        content: "Selecione o ano (CY-1 até CY+2) e o funil (Novos / Base / Ambos) nos controles do cabeçalho. A tabela atualiza imediatamente sem recarregar a página.",
      },
      {
        title: "Grid de metas por produto",
        content: "Cada card de ESN exibe três linhas de produto — Adesão (roxo), SaaS (ciano) e Serviço (verde) — com meta (linha superior) e realizado (linha inferior) para cada mês. A porcentagem de atingimento aparece abaixo em cor proporcional ao resultado.",
      },
      {
        title: "Cores de desempenho",
        content: "Verde (≥100%) indica meta batida; âmbar (80–99%) indica atingimento parcial; rosa/vermelho (<80%) indica resultado abaixo do esperado. Meses sem meta ficam em branco.",
      },
      {
        title: "Subtotais trimestrais",
        content: "Clique no cabeçalho Q1, Q2, Q3 ou Q4 para expandir ou recolher a coluna de subtotal daquele trimestre. O trimestre atual fica destacado em azul e já inicia expandido.",
      },
      {
        title: "Coluna Ano",
        content: "A última coluna de cada linha soma meta e realizado de todos os 12 meses, exibindo o total anual com a mesma lógica de cores de desempenho.",
      },
      {
        title: "Alerta de forecast",
        content: "O card \"Alerta de forecast — threshold\" (visível para manager e admin) define o percentual mínimo de cobertura de pipeline vs meta SaaS que dispara um alerta. Ajuste o valor e clique em Salvar.",
      },
    ],
    tips: ["Use o seletor ESN no cabeçalho para focar em um único executivo; \"Todos\" exibe um card por ESN.", "O trimestre atual inicia com subtotal expandido — recolha os demais para reduzir a largura da tabela.", "Meta e realizado são segregados por funil: sempre confira qual funil está selecionado antes de tirar conclusões.", "Para editar as metas, acesse a tela /metas — esta página é somente leitura para acompanhamento de performance.", "Role horizontalmente dentro de cada card para ver todos os meses; a coluna de produto fica fixa à esquerda."],
  },

  "/admin/feature-flags": {
    title: "Feature Flags",
    roles: ["admin"],
    description: "Controla quais funcionalidades do sistema estão ativas e para quais papéis de usuário cada uma é visível.",
    sections: [
      {
        title: "O que é uma feature flag",
        content: "Cada flag representa uma funcionalidade do sistema (ex.: módulo APN). Quando ativada globalmente, todos os usuários a enxergam. Quando desativada, apenas os papéis marcados em \"acesso por papel\" conseguem acessar.",
      },
      {
        title: "Status da flag",
        content: "O botão de toggle ao lado direito alterna entre Ativo e Inativo. Ativo = funcionalidade liberada para todo o sistema. Inativo = acesso restrito aos papéis definidos em \"acesso por papel\".",
      },
      {
        title: "Acesso por papel (preview controlado)",
        content: "Os botões de papel (admin, manager, esn, apn) definem quem pode acessar a feature quando ela está Inativa. Clique no papel para incluí-lo ou removê-lo da lista. Útil para liberar acesso beta a um grupo específico antes de ativar para todos.",
      },
      {
        title: "Flag apn_workspace",
        content: "Controla todo o módulo APN (APNs e seus espaços de trabalho). Padrão de seed: Inativo com acesso somente para \"admin\". Para liberar em beta para APNs: manter Inativo e adicionar o papel \"apn\".",
      },
      {
        title: "Aplicação imediata",
        content: "Alterações entram em vigor imediatamente — sem necessidade de reiniciar o servidor. Usuários logados sentirão o efeito na próxima navegação ou atualização de página.",
      },
    ],
    tips: ["Use o botão \"Atualizar\" para recarregar o estado atual das flags sem recarregar a página.", "Antes de ativar uma flag para todos, teste com o papel \"admin\" como preview.", "Remover todos os papéis de uma flag inativa torna a feature invisível para todo o sistema.", "A flag apn_workspace controla sidebar, rotas e APIs do módulo APN — ative com cuidado.", "Flags não encontradas pelo middleware bloqueiam o acesso com erro 403 — verifique o banco se uma rota não responde."],
  },

  "/forecast": {
    title: "Forecast",
    roles: ["manager", "admin"],
    description: "Visão anual do pipeline em aberto e dos negócios já fechados, organizada por tipo de produto e funil. Permite comparar o que está previsto com o que foi realizado mês a mês.",
    sections: [
      {
        title: "Painel de Quarters (topo)",
        content: "Quatro cartões Q1–Q4 mostram o realizado SaaS de cada trimestre versus a meta, com uma barra de progresso e percentual de atingimento. O trimestre atual é destacado em ciano com o rótulo \"ATUAL\". Clique em qualquer cartão para expandir ou recolher as colunas de subtotal daquele trimestre nas tabelas abaixo.",
      },
      {
        title: "Gráfico de barras mensal (Bullet Rows)",
        content: "Doze barras horizontais — uma por mês — exibem três camadas sobrepostas: realizado (barra sólida), previsto em pipeline (fundo semitransparente) e meta (marcador vertical amarelo). O valor realizado aparece à direita. Meses já passados são exibidos em verde-água; meses futuros em ciano.",
      },
      {
        title: "Tabelas Pipeline Previsto e Realizado (Fechado/Ganho)",
        content: "Duas tabelas lado a lado com a mesma estrutura: linhas para Adesão, SaaS e Serviço, separadas por funil Novos e Base; colunas para os 12 meses + total anual. Cada célula clicável abre um modal com os leads que compõem aquele valor. No modo \"vs Metas\", cada célula exibe o realizado colorido (verde ≥100 %, âmbar ≥80 %, rosa <80 %) com a meta em cinza abaixo.",
      },
      {
        title: "Subtotais trimestrais",
        content: "Por padrão todos os trimestres estão recolhidos. Clique no cabeçalho Q1, Q2, Q3 ou Q4 nas tabelas para exibir uma coluna extra de subtotal daquele trimestre. Clique novamente para ocultar.",
      },
      {
        title: "Avaliação Trimestral vs Metas",
        content: "Tabela de resumo abaixo das matrizes principais, com Adesão, SaaS e Serviço por trimestre. Exibe o valor realizado + previsto consolidado, a meta e o percentual de atingimento. Expanda \"Detalhamento mensal\" para ver o mesmo breakdown mês a mês.",
      },
      {
        title: "Forecast Ponderado por Estágio",
        content: "Painel com cartões mensais (1, 2, 3 ou 6 meses à frente) que aplica uma probabilidade por estágio ao SaaS em pipeline para estimar o valor esperado de conversão. Cada cartão mostra pipeline bruto, valor ponderado, percentual de conversão esperado e o detalhamento por estágio.",
      },
    ],
    tips: ["Use o toggle \"Realizado / vs Metas\" no canto superior direito para ativar a coloração de performance nas tabelas.", "Clique em qualquer célula com valor para abrir o drilldown e ver quais leads compõem aquele número.", "O seletor de funil (Ambos / Novos / Base) filtra as duas tabelas simultaneamente.", "O trimestre atual já está destacado nas tabelas — você não precisa expandir para enxergar o Q em andamento.", "Use o Forecast Ponderado para projetar receita provável no curto prazo levando em conta a maturidade do pipeline."],
  },

  "/import/realizados": {
    title: "Importar Realizados — Serviços",
    roles: ["admin"],
    description: "Importe o relatório mensal de faturamento de serviços do ERP (XLSX \"Relação Fat Serviços por Projetos\") para atualizar os valores realizados de SaaS e serviços nos leads do CRM.",
    sections: [
      {
        title: "Passo 1 — Upload do arquivo",
        content: "Clique na área pontilhada e selecione o XLSX exportado do ERP. O sistema lê o arquivo, detecta os meses presentes e gera uma prévia completa antes de qualquer alteração no banco.",
      },
      {
        title: "Passo 2 — Conferência dos dados",
        content: "Veja o resumo com total faturado, novos leads que serão criados, atualizações e conflitos. Os meses detectados são exibidos como chips. Conflitos são listados separadamente e serão ignorados na importação.",
      },
      {
        title: "De/Para de ESNs",
        content: "Se o nome de uma ESN no ERP não coincidir com o cadastro do CRM, um painel laranja exibe os nomes não encontrados. Vincule cada um a uma ESN existente ou crie uma nova ESN inativa diretamente nessa tela.",
      },
      {
        title: "Resumo e linhas detalhadas",
        content: "Expanda o \"Resumo por Mês × ESN × Funil\" para conferir os totais agrupados. Expanda \"Linhas detalhadas\" para ver cada registro individualmente e marcar linhas para ignorar com o botão de olho.",
      },
      {
        title: "Confirmação da importação",
        content: "Clique em \"Confirmar X lançamentos\" para gravar. O resultado mostra leads criados, atualizados e ignorados. Leads sem CNPJ podem ter o CNPJ preenchido automaticamente via consulta à Receita Federal logo após a importação.",
      },
      {
        title: "Busca de CNPJs em lote (pós-importação)",
        content: "Se leads importados não tiverem CNPJ, clique em \"Buscar CNPJs automaticamente\". O sistema cruza com dados do CRM e consulta a Receita Federal. Selecione o CNPJ correto para cada empresa e confirme.",
      },
    ],
    tips: ["Linhas marcadas como conflito (mesmo lead, mesmo mês, valores divergentes) são sempre ignoradas — resolva manualmente no CRM.", "Use o botão \"Ignorar linha\" (ícone de olho) para excluir registros específicos antes de confirmar.", "A importação sobrescreve os valores do mês informado — não é um acumulado.", "Crie ESNs inativas no de/para para manter o histórico mesmo de ESNs que saíram do time.", "Clique em \"Novo upload\" a qualquer momento para descartar a prévia e começar do zero."],
  },

  "/import/totvs-email": {
    title: "Importar Lead — E-mail Totvs",
    roles: ["admin"],
    description: "Converta automaticamente um e-mail de \"Geração de Oportunidade Prospect\" do Protheus Workflow em um lead do CRM. Cole o corpo do e-mail, revise os dados extraídos e salve com um clique.",
    sections: [
      {
        title: "Passo 1 — Colar o e-mail",
        content: "Copie o e-mail completo no Outlook (Ctrl+A, Ctrl+C), incluindo o cabeçalho \"De / Para / Assunto\". Cole na área de texto ou use o botão \"Colar da área de transferência\". Clique em \"Analisar e-mail\" para extrair os dados.",
      },
      {
        title: "Passo 2 — Revisar dados extraídos",
        content: "O sistema preenche automaticamente empresa, contato, qualificação e script. Campos editáveis aparecem em destaque — corrija o que for necessário antes de salvar. Campos somente leitura (CNPJ, funil, estágio) foram normalizados automaticamente.",
      },
      {
        title: "Atribuição do ESN responsável",
        content: "O sistema sugere o ESN com base no destinatário do e-mail. Confirme ou selecione manualmente no campo \"ESN responsável\" — obrigatório para salvar o lead.",
      },
      {
        title: "Qualificação e Script",
        content: "Os campos ERP atual, prazo de implantação, número de usuários e principal dor são extraídos do Script do e-mail. Expanda o \"Script completo\" para ver todas as perguntas e respostas do qualificador Totvs.",
      },
      {
        title: "Leads duplicados",
        content: "Se o CNPJ já existir em um lead ativo, um aviso laranja é exibido com detalhes do lead existente. Você pode criar uma nova oportunidade mesmo assim (clique em \"Criar mesmo assim\") ou cancelar.",
      },
      {
        title: "Passo 3 — Lead criado",
        content: "Após salvar, o lead entra diretamente no estágio \"Qualificado — Em levantamento\" do funil de Novos Nomes. Use o botão \"Abrir no funil\" para ir direto ao kanban.",
      },
    ],
    tips: ["Inclua sempre o cabeçalho do e-mail (De, Para, Assunto) para que o sistema identifique o ESN responsável.", "O estágio é definido automaticamente — leads deste e-mail já passaram pela qualificação do APN Totvs.", "Se o parse falhar, verifique se o e-mail é do modelo \"Via Receptivo\" do Protheus Workflow.", "Você pode editar qualquer campo na tela de revisão antes de salvar — o dado original fica nas observações.", "Use \"Importar outro e-mail\" na tela de conclusão para processar vários e-mails em sequência."],
  },

  "/import": {
    title: "Importação Excel",
    roles: ["admin"],
    description: "Importação em lote de dados a partir de planilhas. Suporta dois tipos: clientes do ERP (Contas) e metas mensais dos ESNs.",
    sections: [
      {
        title: "Tipos de importação",
        content: "Aba \"Clientes ERP (Contas)\": importa ou atualiza a base de clientes a partir do ERP. Colunas esperadas: nome, cnpj, segmento, porte, cidade, uf, tel, site, email, obs, esn.\nAba \"Metas dos ESNs\": atualiza as metas mensais de SaaS e Adesão por ESN. Colunas esperadas: esn, meta_saas_mensal, meta_adesao_mensal.",
      },
      {
        title: "Como enviar o arquivo",
        content: "Arraste o arquivo para a área pontilhada ou clique nela para abrir o seletor de arquivos.\nFormatos aceitos: .xlsx, .xls ou .csv. Tamanho máximo: 10 MB.",
      },
      {
        title: "Pré-visualização e barra de estatísticas",
        content: "Antes de confirmar, o sistema exibe uma tabela de pré-visualização com todas as linhas classificadas.\nA barra de estatísticas mostra contagens: Novos (verde), Atualizar (ciano), Duplicatas (laranja) e Erros (rosa).",
      },
      {
        title: "Detecção de duplicatas",
        content: "O sistema detecta registros já existentes pelo CNPJ (Contas) ou pelo nome exato do ESN (Metas) antes de confirmar.\nNa importação de Contas, marque \"Sobrescrever duplicatas\" para atualizar os campos das contas já cadastradas.",
      },
      {
        title: "Confirmar importação",
        content: "Clique em \"Confirmar importação\" para processar as linhas válidas. Linhas com erro são ignoradas automaticamente.\nAo concluir, o sistema exibe o resumo: inseridos, atualizados, ignorados e erros.",
      },
    ],
    tips: ["Os nomes das colunas no arquivo não diferenciam maiúsculas de minúsculas.", "O nome do ESN no arquivo de Metas deve corresponder exatamente ao cadastrado no sistema.", "Linhas com erro na pré-visualização são ignoradas — corrija o arquivo e reimporte se necessário.", "Use \"Trocar arquivo\" na tela de pré-visualização para selecionar outro arquivo sem reiniciar a página.", "A importação de Metas sobrescreve sempre — não há opção de ignorar duplicatas."],
  },

  "/admin/lead-origins": {
    title: "Origens de Lead",
    roles: ["admin"],
    description: "Gerencie a lista de origens disponíveis no campo \"Origem\" do formulário de lead. Cada origem pode ser associada ao funil New Names, Base ou a ambos.",
    sections: [
      {
        title: "O que são origens",
        content: "Origens indicam como um lead chegou até o time — por exemplo: Indicação, LinkedIn, Evento, Site. Essa informação ajuda a medir quais canais geram mais oportunidades.",
      },
      {
        title: "Criar nova origem",
        content: "Clique em \"Nova Origem\", preencha o nome, selecione o funil aplicável (New Names, Base ou Ambos) e defina a ordem de exibição. Clique em \"Salvar\" para confirmar.",
      },
      {
        title: "Editar origem existente",
        content: "Clique no ícone de lápis ao lado da origem para alterar o nome, funil ou ordem. As alterações aparecem imediatamente no formulário de criação de leads.",
      },
      {
        title: "Ativar e desativar",
        content: "O ícone verde/cinza ao lado de cada origem alterna entre ativa e inativa. Origens inativas ficam ocultas no formulário de lead, mas os dados existentes são preservados.",
      },
      {
        title: "Ver origens inativas",
        content: "Marque a opção \"Ver inativas\" no cabeçalho para exibir origens desativadas. Você pode reativá-las ou editá-las a qualquer momento.",
      },
    ],
    tips: ["Use nomes curtos e padronizados — serão exibidos em filtros e relatórios futuramente.", "O campo \"Ordem\" controla a sequência no dropdown do formulário de lead.", "Prefira desativar uma origem obsoleta a excluí-la, para preservar o histórico.", "Origens marcadas como \"Ambos\" aparecem nos funis New Names e Base simultaneamente.", "Origens sem vínculo de funil definido podem não aparecer dependendo do funil do lead."],
  },

  "/metas": {
    title: "Planejamento de Metas",
    roles: ["manager", "admin"],
    description: "Planilha de metas anuais por ESN, tipo de produto e funil. Edite qualquer célula diretamente na tela para lançar ou ajustar os valores planejados.",
    sections: [
      {
        title: "Estrutura da tabela",
        content: "As linhas são organizadas em dois blocos de funil (NOVOS e BASE). Dentro de cada funil há três grupos de produto — Adesão (roxo), SaaS (ciano) e Serviço (verde). Cada grupo lista todos os ESNs com uma linha editável por mês, seguida de uma linha de total por produto.",
      },
      {
        title: "Edição inline por célula",
        content: "Clique em qualquer célula de valor para transformá-la em um campo de texto. Digite o novo valor em R$ (vírgula como decimal), depois pressione Enter ou Tab para confirmar. Pressione Esc para cancelar sem salvar. A alteração é enviada ao servidor imediatamente ao confirmar.",
      },
      {
        title: "Coluna Ano",
        content: "A última coluna à direita é sempre o total anual de cada linha (soma dos 12 meses) e é calculado automaticamente — não é editável. Linhas de total por produto somam todos os ESNs daquele grupo.",
      },
      {
        title: "Seletor de ano",
        content: "Escolha o ano no seletor no canto superior direito. As opções disponíveis são o ano anterior (CY-1), o ano corrente (CY), e os dois anos seguintes (CY+1 e CY+2), permitindo planejar com antecedência.",
      },
    ],
    tips: ["Use Tab para avançar célula a célula sem precisar clicar em cada uma.", "Células sem valor aparecem como \"—\"; clique nelas para inserir um novo valor.", "Alterações são salvas individualmente por célula — não há botão de salvar global.", "Role a tabela horizontalmente para ver todos os 12 meses; a coluna de labels fica fixa à esquerda.", "Para apagar uma meta, edite a célula e deixe em branco ou zero e confirme com Enter."],
  },

  "/my-area": {
    title: "Minha Área",
    roles: ["esn"],
    description: "Painel pessoal do representante com ações do dia, leads que precisam de atenção e acompanhamento de metas. Use esta tela no início de cada dia para priorizar seu trabalho.",
    sections: [
      {
        title: "Ações do Dia",
        content: "Lista todas as próximas ações agendadas para hoje. Clique no círculo ao lado de uma ação para marcá-la como concluída. Clique no ícone de link para abrir o lead diretamente.",
      },
      {
        title: "Leads que Precisam de Atenção",
        content: "Exibe leads com três tipos de urgência: rosa (Ação vencida — follow-up atrasado), laranja (Prev. vencida — data de fechamento já passou) e verde-amarelo (Sem interação — muitos dias sem contato). Clique no lead para abrir e registrar uma interação.",
      },
      {
        title: "Meta vs Realizado",
        content: "Barras de progresso para SaaS, Adesão e Serviço mostrando quanto você realizou da meta do mês. O indicador \"Ritmo\" estima se você está no passo certo considerando os dias já decorridos. Se tiver metas em dois funis (Novos e Base), use o toggle para alternar.",
      },
      {
        title: "Resumo do Pipeline",
        content: "Seção colapsável com quatro indicadores: Leads Ativos, Valor Pipeline, Sem Interação (+7 dias) e Ações Vencidas. Clique no cabeçalho para expandir ou recolher. Use para ter uma visão rápida da saúde da sua carteira.",
      },
      {
        title: "Minhas Conquistas",
        content: "Aparece somente quando você tem badges ou posição no ranking do mês. Mostra sua colocação no ranking da equipe e os badges conquistados no mês atual. Badges são concedidos automaticamente ao atingir marcos de desempenho.",
      },
    ],
    tips: ["Comece o dia sempre pela seção \"Ações do Dia\" — são seus compromissos agendados.", "Leads com destaque rosa (Ação vencida) são os mais urgentes: o cliente está esperando retorno.", "O indicador \"Ritmo\" abaixo de 80% em rosa indica que você precisa acelerar para bater a meta.", "O Resumo do Pipeline fica recolhido por padrão — clique para expandir quando precisar ver os números.", "Badges e posição no ranking só aparecem quando há dados do mês atual disponíveis."],
  },

  "/new-names": {
    title: "Funil Novos Nomes",
    roles: ["esn", "manager", "admin"],
    description: "Pipeline de prospecção para novos clientes — empresas que ainda não usam Totvs Moda. Acompanhe cada oportunidade desde a prospecção até o fechamento ou descarte.",
    sections: [
      {
        title: "Criando um lead",
        content: "Clique em \"+ Novo Lead\" na barra de filtros. Digite o CNPJ para buscar os dados da Receita Federal automaticamente (Razão Social e Nome Fantasia são preenchidos). Preencha ESN, produto, valores e previsão de fechamento, depois salve.",
      },
      {
        title: "Navegando no Kanban",
        content: "As colunas seguem as etapas do funil: Prospecção → LEAD - Aguardando Qualificação → Qualificado - Em levantamento → Visão Executiva → Negociação → Ganho / Perdido / Postergado. Arraste o card para mover o lead entre etapas. Em desktop, clicar no card abre o painel rápido (Quick View) à direita.",
      },
      {
        title: "Quick View (desktop)",
        content: "Painel lateral de 70% que abre ao clicar em qualquer card no kanban. Exibe dados resumidos e permite registrar interações, gerenciar tarefas e mudar o stage sem abrir o modal completo. Para editar todos os campos do lead, clique no botão \"Editar\" dentro do Quick View.",
      },
      {
        title: "Modal completo de lead — abas",
        content: "O modal abre ao clicar em \"Editar\" no Quick View (ou ao clicar em um card no mobile). Abas disponíveis: Dados (formulário completo), Contatos, Qualificação (score BANT), Interações, Propostas, Histórico (log de alterações), Timeline (feed unificado) e ✨ IA. As últimas cinco abas ficam disponíveis somente após o lead ser salvo pela primeira vez.",
      },
      {
        title: "Filtros e Smart Views",
        content: "Use a FilterBar para filtrar por ESN, produto, stage ou previsão. Para salvar o conjunto atual de filtros como uma view nomeada, clique em \"Salvar View\" — você pode compartilhar a view com toda a equipe. Views salvas aparecem como chips clicáveis acima da barra de filtros.",
      },
      {
        title: "Visualização em tabela e exportação",
        content: "Alterne entre kanban e tabela usando o toggle de visualização na FilterBar. Na tabela é possível ordenar colunas e editar vários leads em sequência. Para exportar os leads com os filtros ativos para CSV, clique em \"Exportar CSV\".",
      },
    ],
    tips: ["Ao mover para Postergado, o sistema exige que você escolha uma data futura — esse lead voltará automaticamente para Prospecção quando o mês chegar.", "Campos obrigatórios configurados pelo admin bloqueiam a movimentação: um modal aparece pedindo que você preencha os dados antes de avançar.", "A aba \"Qualificação\" calcula o score BANT automaticamente (até 100 pts). Leads bem qualificados têm score alto antes de chegar em Negociação.", "A coluna \"Captação APN\" aparece somente quando a feature está habilitada pelo administrador.", "Use Smart Views para acessar rapidamente combinações de filtro que você usa com frequência, como \"Minha carteira em Negociação este mês\"."],
  },

  "/notifications": {
    title: "Notificações",
    roles: ["esn", "manager", "admin"],
    description: "Central de notificações com alertas automáticos sobre leads que precisam de atenção. As notificações são agrupadas por data e filtráveis por tipo e status de leitura.",
    sections: [
      {
        title: "Tipos de notificação",
        content: "Sem interação (laranja/relógio): lead ficou muito tempo sem contato registrado.\nAção vencida (rosa/triângulo): ação de acompanhamento agendada passou da data.\nPrevisão vencida (laranja/relógio): data de fechamento prevista já passou.\nMudança de stage (ciano/tendência): lead teve seu estágio alterado no funil.\nMeta em risco (rosa/tendência): meta de SaaS do ESN está abaixo do esperado.\nLead parado (cinza/pausa): lead sem nenhuma atividade há muito tempo.",
      },
      {
        title: "Agrupamento por data",
        content: "As notificações são organizadas em grupos: \"Hoje\", \"Ontem\" e datas anteriores no formato dia mês ano.\nDentro de cada grupo, as notificações mais recentes aparecem primeiro.",
      },
      {
        title: "Filtros",
        content: "Filtre por status de leitura: Todas, Não lidas ou Lidas.\nFiltre por tipo de notificação usando o seletor ao lado dos botões de status.\nOs filtros se combinam — é possível ver apenas notificações não lidas de um tipo específico.",
      },
      {
        title: "Marcar como lida",
        content: "Clique no ícone de duplo check à direita de uma notificação individual para marcá-la como lida.\nUse o botão \"Marcar todas como lidas\" no cabeçalho para limpar todas de uma vez; ele aparece apenas quando há notificações não lidas.",
      },
    ],
    tips: ["Notificações lidas aparecem com opacidade reduzida para facilitar a identificação das pendentes.", "Clique na notificação para ir diretamente ao lead relacionado.", "O contador de não lidas no cabeçalho é atualizado em tempo real via SSE.", "Use o filtro \"Não lidas\" para focar apenas no que precisa de atenção.", "Notificações do tipo \"Meta em risco\" são geradas automaticamente pelo sistema e visíveis para admins e gerentes."],
  },

  "/perfil": {
    title: "Meu Perfil",
    roles: ["esn", "manager", "admin"],
    description: "Gerencie seus dados pessoais, altere a senha, configure a autenticação de dois fatores (2FA) e ajuste preferências de notificação e calendário.",
    sections: [
      {
        title: "Meus dados",
        content: "Exibe usuário, e-mail, papel e ESN vinculado (se aplicável). Para editar o e-mail, passe o cursor sobre ele e clique no ícone de lápis — pressione Enter para salvar ou Escape para cancelar.",
      },
      {
        title: "Trocar senha",
        content: "Informe a senha atual, a nova senha (mínimo 8 caracteres) e confirme. Clique em \"Salvar nova senha\". Se o admin exigiu troca obrigatória, você é redirecionado para esta tela antes de usar o sistema.",
      },
      {
        title: "Autenticação de dois fatores (2FA)",
        content: "Adiciona uma camada extra de segurança via TOTP (Google Authenticator, Authy, etc.). Clique em \"Configurar 2FA\", escaneie o QR code no seu app autenticador e confirme com o código gerado.",
      },
      {
        title: "Canais de notificação",
        content: "Configure como deseja receber alertas do CRM: Telegram, e-mail ou notificações internas. Cada canal pode ser habilitado ou desabilitado independentemente.",
      },
      {
        title: "Google Calendar",
        content: "Vincula sua conta do Google Calendar para sincronizar eventos e agendamentos do CRM. Disponível na seção de integrações do perfil.",
      },
    ],
    tips: ["Ative o 2FA para proteger sua conta mesmo que a senha seja comprometida.", "Para editar o e-mail, não é necessário entrar no modo de edição — basta clicar no ícone de lápis que aparece ao passar o cursor.", "Senhas devem ter ao menos 8 caracteres — combine letras, números e símbolos.", "Notificações via Telegram exigem vincular seu bot na aba de canais do perfil.", "Se o admin redefiniu sua senha, você será direcionado a esta tela automaticamente no próximo login."],
  },

  "/playbooks": {
    title: "Playbooks",
    roles: ["manager", "admin"],
    description: "Playbooks são sequências de follow-up com passos programados por dia, aplicáveis a qualquer lead. Cada passo pode notificar o ESN responsável, enviar e-mail automático, criar tarefa ou mover o lead de stage.",
    sections: [
      {
        title: "Visualizar passos",
        content: "Clique em qualquer playbook da lista para expandir e ver todos os passos configurados. Cada passo mostra o dia de execução, a ação e a mensagem associada.",
      },
      {
        title: "Criar um playbook (admin)",
        content: "Clique em \"Novo Playbook\", defina nome, funil de destino (Novos, Base ou Ambos) e adicione passos. Cada passo tem um dia de disparo, tipo de ação e descrição.",
      },
      {
        title: "Tipos de ação nos passos",
        content: "Os passos suportam: notificação ao ESN responsável, notificação por papel (ex: gerente), movimentação de stage, envio de e-mail automático e criação de tarefa.",
      },
      {
        title: "Execução automática",
        content: "Marque \"Executar automaticamente no cron diário\" em um passo para que ele seja disparado pelo sistema sem intervenção manual no dia configurado.",
      },
      {
        title: "Ativar e desativar",
        content: "Playbooks inativos ficam com opacidade reduzida e não são oferecidos para aplicação em leads. Clique no badge \"Ativo / Inativo\" para alternar o estado.",
      },
      {
        title: "Aplicar em um lead",
        content: "A aplicação é feita na tela de detalhe do lead, pelo botão \"Playbooks\". Esta tela de gerenciamento é apenas para criar e configurar os playbooks.",
      },
    ],
    tips: ["Crie playbooks distintos para Novos e Base, pois os stages dos funis são diferentes.", "Use o campo \"Descrição do passo\" para deixar claro o que o ESN deve fazer naquele dia.", "Passos com auto-execução são ideais para e-mails; notificações manuais deixam o ESN decidir o momento.", "Playbooks com muitas aplicações indicam boa adoção — monitore pelo contador na lista.", "Exclua playbooks obsoletos para não poluir a lista na tela do lead."],
  },

  "/products": {
    title: "Produtos",
    roles: ["admin"],
    description: "Cadastro e manutenção do catálogo de produtos do portfólio Totvs. Os produtos cadastrados aqui aparecem no formulário de criação e edição de leads.",
    sections: [
      {
        title: "Criar e editar produtos",
        content: "Clique em \"Novo Produto\" para abrir o modal de cadastro. O único campo obrigatório é o nome do produto.\nPara editar, clique no ícone de lápis na linha do produto desejado.",
      },
      {
        title: "Ativar e desativar produtos",
        content: "O ícone de toggle ao lado de cada produto ativo permite desativá-lo. Produtos inativos aparecem com texto riscado em uma seção separada.\nPara reativar, clique no toggle na seção \"Inativos\".",
      },
      {
        title: "Produtos inativos",
        content: "Produtos desativados não aparecem no seletor do formulário de lead, impedindo novos cadastros com esse produto.\nLeads já existentes com o produto inativo continuam com o vínculo preservado.",
      },
      {
        title: "Excluir produto",
        content: "O sistema bloqueia a exclusão de produtos vinculados a leads existentes e exibe uma mensagem explicativa.\nDesative o produto em vez de excluí-lo quando houver leads associados.",
      },
    ],
    tips: ["Desative produtos descontinuados em vez de excluí-los para preservar o histórico.", "O nome do produto é normalizado automaticamente ao salvar (ex: \"SERVIÇOS\" → \"SERVIÇO\").", "Produtos inativos não aparecem no formulário de novo lead.", "ESNs não podem desativar ou excluir produtos — apenas admins."],
  },

  "/reconciliacao": {
    title: "Conciliação de Forecast",
    roles: ["manager", "admin"],
    description: "Lista os leads com previsão de fechamento vencida para que o vendedor ou gerente tome uma decisão: confirmar como ganho, registrar como perdido ou postergar a data.",
    sections: [
      {
        title: "O que aparece aqui",
        content: "Cada card representa um lead cujo campo \"previsão de fechamento\" já passou da data atual. O sistema cruza automaticamente esses leads com os dados do ERP para identificar possíveis contratos fechados.",
      },
      {
        title: "Indicador de match",
        content: "\"Match provável\" (verde) significa que o sistema encontrou uma entrada no faturamento com alta correspondência ao lead — CNPJ e valor semelhantes. \"Confirmar\" (âmbar) indica uma correspondência fraca que precisa de validação manual.",
      },
      {
        title: "Confirmar Ganho",
        content: "Disponível quando há um match identificado. Marca o lead como \"Ganho\" no funil e remove o item da lista de conciliação. Use quando o faturamento exibido corresponde de fato à venda desse lead.",
      },
      {
        title: "Perdido",
        content: "Solicita um motivo antes de mover o lead para o stage \"Perdido\". O motivo é salvo no histórico do lead para análise futura.",
      },
      {
        title: "Postergar (+1 mês / outra data)",
        content: "\"+1 mês\" avança a previsão em exatamente um mês a partir de hoje. \"Outra data\" abre um seletor para definir uma data específica. O lead sai da lista de conciliação e retornará quando a nova data vencer.",
      },
    ],
    tips: ["Processe a conciliação mensalmente após o fechamento do ERP para manter o forecast preciso.", "Leads sem match exibem apenas \"Perdido\" e \"Postergar\" — sem botão de Ganho.", "A tela fica vazia quando todos os itens foram resolvidos — isso indica forecast saudável.", "Confirme os matches \"âmbar\" com cuidado: verifique CNPJ e valor antes de marcar como Ganho."],
  },

  "/reports/forecast": {
    title: "Relatório de Forecast",
    roles: ["manager", "admin"],
    description: "Exibe o pipeline previsto de fechamento para um período de até 3 meses, separado por funil (Novos e Base) e por tipo de receita (Adesão, SaaS e Serviço). Pode ser exportado como planilha Excel.",
    sections: [
      {
        title: "Selecionar o período",
        content: "Escolha o mês de início e o mês de fim nos campos de data. O período máximo é de 3 meses. Clique em \"Atualizar\" para recarregar os dados com a nova seleção.",
      },
      {
        title: "Resumo consolidado",
        content: "A tabela \"Resumo — Pipeline Previsto\" mostra os totais de Adesão, SaaS e Serviço separados por Funil Novos e Funil Base, com linha de Total Geral ao final. Inclui apenas leads nos estágios Qualificado, Visão Executiva e Negociação.",
      },
      {
        title: "Detalhamento por funil",
        content: "Abaixo do resumo, há uma tabela detalhada para cada funil listando conta, ESN responsável, estágio atual, valores de Adesão/SaaS/Serviço e informações de parcelamento de serviço.",
      },
      {
        title: "Exportar para Excel",
        content: "O botão \"Exportar XLSX\" no canto superior direito baixa uma planilha formatada com todos os dados do período selecionado. Útil para compartilhar o forecast em reuniões ou enviar por e-mail.",
      },
      {
        title: "Visualização mobile",
        content: "Em telas menores, cada lead é exibido como um cartão individual com todos os campos, em vez da tabela de desktop. O conteúdo é o mesmo.",
      },
    ],
    tips: ["Mantenha o período em 1 mês para a reunião de pipeline semanal; use 3 meses para planejamento trimestral.", "Leads sem valor preenchido não aparecem no relatório — certifique-se de registrar os valores ao qualificar.", "O Total Geral no resumo é o número mais importante para a meta do trimestre.", "Exporte o XLSX antes de apresentar; o arquivo fica salvo localmente para uso offline.", "Leads em \"Postergado\" ou \"Prospecção\" não entram neste relatório por design."],
  },

  "/reports": {
    title: "Relatórios",
    roles: ["manager", "admin"],
    description: "Central de análise de desempenho histórico de vendas. Oferece relatórios pré-formatados, um painel de atividade por ESN e um construtor livre para consultas personalizadas.",
    sections: [
      {
        title: "Três modos de relatório",
        content: "Use as abas no canto superior direito para alternar entre \"Padrão\" (relatórios prontos), \"Atividade\" (volume de interações por ESN) e \"Builder\" (relatório personalizado passo a passo).",
      },
      {
        title: "Relatórios padrão disponíveis",
        content: "Taxa de Conversão por estágio, Ciclo de Vendas, Análise Ganho/Perda, Comparativo ESN (admin/manager), Leads por Origem e Ganhos por Origem. Selecione o tipo, escolha o funil e o período e clique em \"Gerar Relatório\".",
      },
      {
        title: "Exportar CSV",
        content: "Após gerar qualquer relatório padrão, o botão \"Exportar CSV\" é habilitado. O arquivo baixado inclui todos os dados da consulta com o período e tipo no nome do arquivo.",
      },
      {
        title: "Relatório de Atividade",
        content: "Mostra ligações, WhatsApp, e-mails, reuniões, leads criados e média de interações por lead de cada ESN. Linhas com alerta em rosa indicam ESNs com menos de 15 interações no período.",
      },
      {
        title: "Builder — relatório personalizado",
        content: "Monte uma consulta em 4 passos: escolha a fonte (Leads, Interações ou Propostas), selecione as colunas desejadas, aplique filtros opcionais e execute. Até 100 linhas são exibidas na tela; exporte CSV para o conjunto completo.",
      },
      {
        title: "Salvar relatórios no Builder",
        content: "Na etapa de prévia, dê um nome ao relatório e clique em \"Salvar\" para reutilizá-lo depois. Os relatórios salvos aparecem no topo da aba Builder e podem ser carregados ou excluídos a qualquer momento.",
      },
    ],
    tips: ["Comece pelos relatórios padrão; use o Builder apenas quando precisar de colunas ou filtros específicos.", "O \"Comparativo ESN\" é o relatório mais completo para avaliar desempenho entre revendedores.", "No Builder, deixar as colunas em branco retorna todas as colunas disponíveis da fonte.", "Filtre por funil (Novos ou Base) antes de gerar para evitar misturar métricas de pipelines diferentes.", "Colunas no Relatório de Atividade são clicáveis para reordenar a tabela."],
  },

  "/admin/required-fields": {
    title: "Campos Obrigatórios por Stage",
    roles: ["admin"],
    description: "Configure quais campos devem ser preenchidos antes de um lead avançar para cada etapa dos funis New Names e Base. O sistema exige o preenchimento automaticamente durante o drag-and-drop.",
    sections: [
      {
        title: "Como funciona",
        content: "Para cada etapa dos funis, marque os campos que devem estar preenchidos antes de mover o lead. Se o lead não tiver o campo, um modal de preenchimento é exibido ao arrastar o card.",
      },
      {
        title: "Funis disponíveis",
        content: "A tela exibe duas tabelas: New Names (novos clientes) e Base (carteira existente). Cada tabela lista as etapas intermediárias — Ganho, Perdido e Postergado não são configuráveis aqui.",
      },
      {
        title: "Campos configuráveis",
        content: "Previsão de fechamento, Valor SaaS, Valor Adesão, ESN, Produto, nome do Contato, E-mail e Telefone. Marque apenas o que for realmente essencial para a etapa.",
      },
      {
        title: "Salvar configuração",
        content: "Após ajustar os checkboxes, clique em \"Salvar Configuração\". A regra entra em vigor imediatamente para todos os usuários.",
      },
      {
        title: "Boas práticas",
        content: "Evite exigir muitos campos nas primeiras etapas — isso trava a prospecção. Reserve campos financeiros para estágios como Negociação.",
      },
    ],
    tips: ["Campos marcados em etapas iniciais bloqueiam o drag-and-drop mesmo para leads recém-criados.", "A configuração é por funil: New Names e Base podem ter regras diferentes para o mesmo campo.", "Se um campo não estiver visível no modal de preenchimento, verifique se ele existe no cadastro do lead.", "Alterações são salvas globalmente — afetam todos os vendedores ao mesmo tempo.", "Para desfazer, desmarque os checkboxes e salve novamente."],
  },

  "/admin/rotting-config": {
    title: "Deal Rotting — Configuração",
    roles: ["admin"],
    description: "Define quantos dias sem interação com o prospect acionam cada nível de alerta de \"deal rotting\" nos cards do funil, separadamente para os funis New Names e Base.",
    sections: [
      {
        title: "O que é deal rotting",
        content: "Um lead entra em estado de \"rotting\" quando não há nenhuma interação registrada com o prospect por um número de dias configurável. Os cards no funil recebem indicadores visuais (amarelo, laranja, vermelho) conforme o nível de alerta atingido.",
      },
      {
        title: "Tipos de interação contabilizadas",
        content: "Apenas interações registradas na aba de Interações do lead são contadas: E-mail, WhatsApp, Telefone, Reunião, Videochamada e Visita. Atualizações de campo ou movimentos de stage não reiniciam o contador.",
      },
      {
        title: "Níveis de alerta",
        content: "Atenção (amarelo): sinal de que o lead precisa de atenção em breve. Risco (laranja): lead com risco real de esfriar — prioridade de contato. Crítico (vermelho): lead sem interação há muito tempo — ação imediata necessária.",
      },
      {
        title: "Configuração por funil",
        content: "New Names e Base têm thresholds independentes porque o ciclo de vendas de novos clientes costuma ser mais curto. Padrões sugeridos: New Names 7/15/30 dias; Base 15/30/45 dias.",
      },
      {
        title: "Salvar alterações",
        content: "Cada funil tem seu próprio botão \"Salvar\". Alterar os valores de um funil e clicar Salvar não afeta o outro. Os novos thresholds são aplicados imediatamente a todos os leads ao recarregar o funil.",
      },
    ],
    tips: ["Ajuste os thresholds conforme o ciclo médio de vendas do seu time — valores muito baixos geram alertas excessivos.", "Salve cada funil separadamente — o botão Salvar é por bloco (New Names / Base).", "O threshold de Atenção deve ser sempre menor que Risco, que deve ser menor que Crítico.", "Leads nos stages Ganho, Perdido e Postergado não são afetados pelo rotting.", "Após salvar, recarregue o Funil para ver os novos indicadores aplicados nos cards."],
  },

  "/sync": {
    title: "Integração ERP TOTVS",
    roles: ["admin"],
    description: "Configuração e monitoramento da sincronização automática com o ERP TOTVS. Mantém clientes e valores atualizados entre os dois sistemas.",
    sections: [
      {
        title: "Tipos de integração",
        content: "Desativado: sem sincronização automática.\nTOTVS REST API: conecta diretamente à API REST do ERP via URL base e credenciais.\nArquivo (File Drop): monitora uma pasta no servidor e processa o arquivo .xlsx/.csv mais recente encontrado.",
      },
      {
        title: "Tipos de autenticação (REST API)",
        content: "Sem autenticação: acesso público sem credenciais.\nBasic: usuário e senha enviados no cabeçalho HTTP.\nBearer Token: token de acesso enviado no cabeçalho Authorization.",
      },
      {
        title: "Configuração colapsável",
        content: "A seção de configuração pode ser expandida ou recolhida clicando no cabeçalho \"Configuração da Integração\".\nEla abre automaticamente quando a integração está ativa (não está no modo Desativado).",
      },
      {
        title: "Sincronização manual",
        content: "Clique em \"Sincronizar agora\" para disparar uma sincronização imediata. O botão fica desabilitado quando a integração está desativada.\nO resultado exibe: contas criadas, atualizadas e leads atualizados.",
      },
      {
        title: "Histórico de sincronizações",
        content: "A tabela de histórico registra todas as execuções com data, duração e status.\nStatus possíveis: Sucesso (verde), Erro (rosa), Em andamento (ciano), Parcial (laranja), Ignorado (cinza).",
      },
    ],
    tips: ["Configure o intervalo automático em minutos; zero significa somente execução manual.", "O mapeamento de campos permite indicar quais colunas do ERP correspondem a CNPJ, Nome e Valores.", "No modo File Drop, o arquivo mais recente da pasta configurada é processado a cada execução.", "Verifique o histórico após a primeira sincronização para identificar possíveis erros de mapeamento."],
  },

  "/team": {
    title: "Equipe",
    roles: ["admin"],
    description: "Gerencie todos os usuários do CRM: crie novos membros, ajuste papéis, ative ou desative contas. A aba APNs só aparece quando o módulo de prospecção estiver habilitado.",
    sections: [
      {
        title: "Abas de filtragem",
        content: "Filtre por Todos (ativos), ESNs, Admins, Managers, Inativos ou APNs. O número ao lado de cada aba mostra a contagem atual. Use a busca para encontrar por nome, e-mail ou username.",
      },
      {
        title: "Criar novo membro",
        content: "Clique em \"Novo Membro\" para expandir o formulário. Preencha username, e-mail, senha (mín. 8 caracteres) e papel. Para o papel ESN, vincule a ESN correspondente e defina o acesso ao funil.",
      },
      {
        title: "Papéis disponíveis",
        content: "Administrador (acesso total), Gerente (relatórios e performance), Executivo ESN (acesso ao próprio funil) e Agente de Prospecção APN (módulo APN, quando ativo). O papel define o que cada usuário vê no sistema.",
      },
      {
        title: "Ativar e desativar usuário",
        content: "Clique no ícone de toggle ao lado do membro e confirme a ação no modal. Usuários inativos não conseguem fazer login. Você não pode alterar seu próprio status.",
      },
      {
        title: "Badge \"Trocar senha\"",
        content: "Indica que o usuário ainda não redefiniu a senha padrão definida no cadastro. O sistema exige a troca no primeiro acesso.",
      },
      {
        title: "Gestão de APNs",
        content: "Na aba APNs, cadastre Agentes de Prospecção informando username, e-mail, nome completo e território. Visível apenas para admins quando a feature flag \"apn_workspace\" está ativa.",
      },
    ],
    tips: ["O acesso ao funil de um ESN (Novos, Base ou Ambos) é configurado no momento do cadastro.", "ESNs sem esn_id vinculado enxergam dados de todas as ESNs — sempre associe ao criar.", "Use a aba Inativos para reativar um usuário que voltou ao time.", "Altere o papel de um usuário editando diretamente no banco — a tela de equipe não oferece edição inline de papel por segurança.", "Novos membros são criados ativos e o sistema força a troca de senha no primeiro login."],
  },

  "/users": {
    title: "Gestão de Usuários",
    roles: ["admin"],
    description: "Cadastro e gerenciamento de todos os usuários do sistema. Permite criar contas, definir papéis e controlar o acesso de cada pessoa ao CRM.",
    sections: [
      {
        title: "Papéis disponíveis",
        content: "Administrador: acesso total ao sistema.\nGerente: supervisão de metas e equipe, sem acesso a configurações avançadas.\nExecutivo (ESN): acesso restrito aos próprios leads e oportunidades.",
      },
      {
        title: "Campo Funis (funnel_access)",
        content: "Exclusivo para usuários com papel ESN. Define quais funis o executivo pode acessar: Ambos (Novos + Base), Apenas Novos Nomes ou Apenas Base.\nO badge colorido na listagem indica o valor atual: ciano = Ambos, verde = Novos, laranja = Base.",
      },
      {
        title: "Vínculo ESN",
        content: "Usuários com papel ESN precisam estar vinculados a um registro de ESN para que o escopo de dados funcione corretamente.\nO aviso laranja \"Não vinculado\" aparece quando o vínculo está ausente.",
      },
      {
        title: "Auto-vincular ESNs por e-mail",
        content: "O botão \"Auto-vincular ESNs por e-mail\" associa automaticamente usuários ESN ao cadastro de ESN que possui o mesmo endereço de e-mail.\nUse após criar usuários em lote ou importar ESNs.",
      },
      {
        title: "Redefinição de senha",
        content: "Ao editar um usuário, deixe o campo de senha em branco para manter a senha atual.\nSenhas devem ter no mínimo 8 caracteres e são armazenadas com criptografia bcrypt.",
      },
    ],
    tips: ["ESN sem vínculo aparece com aviso laranja — resolva com o botão de auto-vínculo.", "O campo Funis só aparece quando o papel selecionado for ESN.", "Não é possível excluir o próprio usuário administrador logado.", "Cada papel tem um nível numérico — quanto maior, mais acesso.", "Gerentes veem dados de toda a equipe, mas não acessam configurações de sistema."],
  },

  "/webhooks": {
    title: "Webhooks",
    roles: ["admin"],
    description: "Configure endpoints externos para receber notificações HTTP em tempo real quando eventos ocorrem no CRM, como criação de leads, mudança de stage ou aceitação de proposta. As requisições são assinadas com HMAC-SHA256.",
    sections: [
      {
        title: "Criar um webhook",
        content: "Clique em \"Novo Webhook\", informe a URL de destino (deve começar com https://) e selecione os eventos que devem disparar a notificação. O secret é gerado automaticamente se deixado em branco.",
      },
      {
        title: "Secret de assinatura",
        content: "Após criar um webhook, o secret é exibido uma única vez. Guarde-o imediatamente — ele é necessário para validar a autenticidade das requisições recebidas no seu servidor.",
      },
      {
        title: "Eventos disponíveis",
        content: "Os eventos cobrem o ciclo de vida do lead (criado, stage alterado, ganho, perdido), criação de interações e envio/aceitação de propostas. Selecione apenas os eventos relevantes para seu sistema.",
      },
      {
        title: "Ativar e desativar",
        content: "O ícone de círculo verde/cinza no card indica se o webhook está ativo. Clique nele para pausar ou retomar os disparos sem excluir a configuração.",
      },
      {
        title: "Disparar um teste",
        content: "Clique no botão Play (triângulo) para enviar um evento de teste ao endpoint. Use para verificar se o servidor externo está recebendo e processando corretamente.",
      },
      {
        title: "Log de disparos",
        content: "Clique no ícone de lista para ver o histórico de chamadas do webhook: evento, status HTTP de resposta e data/hora. Erros de rede aparecem como \"ERR\" na coluna de status.",
      },
    ],
    tips: ["Salve o secret imediatamente após criar o webhook — não há como recuperá-lo depois.", "Prefira https:// para garantir que os dados trafeguem criptografados.", "Use o disparo de teste antes de colocar em produção para validar seu endpoint.", "Desative webhooks com muitos erros no log para evitar sobrecarga desnecessária.", "Subscreva apenas nos eventos que seu sistema realmente processa para reduzir volume."],
  },

  "/win-loss": {
    title: "Win / Loss",
    roles: ["manager", "admin"],
    description: "Analisa todos os negócios finalizados (ganhos e perdidos) para revelar taxa de vitória, motivos de perda, estágios críticos e desempenho por ESN. Filtre por período, funil e revendedor.",
    sections: [
      {
        title: "KPIs no topo",
        content: "Cinco cards mostram: Taxa de Vitória, Valor Fechado/Ganho, Valor Perdido, Ciclo Médio em dias e total de Leads Fechados no período. Esses números mudam conforme os filtros aplicados.",
      },
      {
        title: "Filtros de análise",
        content: "Selecione o período (30, 60, 90 dias ou 1 ano), o funil (Novos, Base ou ambos) e um ESN específico no canto superior direito. Todos os gráficos e tabelas atualizam ao mudar qualquer filtro.",
      },
      {
        title: "Motivos de perda e estágio de saída",
        content: "Dois gráficos de barras horizontais mostram quais motivos causaram mais perdas e em qual estágio os leads são perdidos com maior frequência. Útil para identificar gargalos no processo.",
      },
      {
        title: "Análise por produto e por ESN",
        content: "O painel \"Taxa de Perda por Produto\" mostra ganhos vs. perdas por produto com barras verde/rosa. O painel \"Taxa de Perda por ESN\" classifica revendedores: verde (<25%), laranja (25–40%) e vermelho (>40%).",
      },
      {
        title: "Matriz ESN × Motivo",
        content: "A tabela cruzada mostra quantas perdas cada ESN registrou por motivo. A célula em destaque rosa indica a maior concentração de perdas daquele revendedor, revelando padrões específicos.",
      },
      {
        title: "Velocidade por estágio",
        content: "Gráfico de barras mostrando quantos dias em média os leads ficam em cada estágio antes de avançar. Barras laranjas (>30d) e limão (14–30d) indicam onde o pipeline trava.",
      },
    ],
    tips: ["Comece com período de 90 dias para ter volume suficiente de dados antes de tirar conclusões.", "Taxa de vitória abaixo de 30% em qualquer ESN merece análise do motivo de perda predominante daquele revendedor.", "A velocidade por estágio requer histórico de movimentações — pode aparecer vazia em instâncias novas.", "Filtre por um ESN específico quando precisar dar um feedback detalhado a um revendedor.", "Compare o \"Valor Perdido\" com o \"Valor Fechado/Ganho\" para entender o potencial não convertido."],
  }
};
